import { Link, useNavigation, useNavigate } from "react-router-dom";
import classes from "./CheckUpPatientDetails.module.css";
import { useContext, useState, useEffect, useRef } from "react";
import WaitingContext from "../../../store/waitingListContext";
import AuthContext from "../../../store/auth-context";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import PatientHistory from "./PatientHistory";
import Modal from "../../UI/Modal";
import CheckedDetails from "./CheckedDetails";
import MedicineDetails from "./MedicineDetails";
import Certificates from "./Certificates";
import PrescriptionPrint, { printPrescription } from "../Prescriptions/PrescriptionPrint";
import { parseHeader } from "../../../util/headerUtil";
import { exportPrescriptionPDF } from "../../../util/pdfExportUtil";
import { FaSave, FaFileExport, FaArrowLeft, FaWhatsapp } from 'react-icons/fa';
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AIChatBot from "../../UI/AIChatBot";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CheckUpPatientDetails = () => {
  const { t } = useTranslation();
  const billInput = useRef();
  const amountReceivedInput = useRef();
  const checkPatient = useContext(WaitingContext);
  const authCtx = useContext(AuthContext);
  const checkPatientData = checkPatient?.waitingPatient;
  const hospitaData = authCtx.hospitalData; // ✅ Use hospital branding data from authCtx
  const navigation = useNavigation();
  const navigate = useNavigate();
  const [success, setIsSuccess] = useState(null);
  const [error, setIsError] = useState(null);
  const [checkedData, setCheckedData] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [isSaveBill, setIsSaveBill] = useState(false);
  const [isPrintWhileSubmit, setIsPrintWhileSubmit] = useState(false);
  const [prescrbedMedicines, setPrescrbedMedicines] = useState([]);
  const [outstandingAmount, setOutstandingAmount] = useState(0);
  const [doPrint, setDoPrint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.info(t("You are back online"), { position: "top-right", autoClose: 3000 });
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning(t("Internet connection lost. You are now offline."), { position: "top-right", autoClose: 5000 });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [t]);

  useEffect(() => {
    if (!checkPatientData) {
      navigate(-1);
    }
  }, [checkPatientData, navigate]);
  // 🆕 Sync with configuration once loaded
  useEffect(() => {
    if (authCtx.hospitalData) {
      const { CheckUpSaveBill, CheckUpSavePrescription, OPDPrintPrescription, opdFee, hospitalDefaultBill } = authCtx.hospitalData;

      console.log("Syncing Check Patient Config:", { CheckUpSaveBill, CheckUpSavePrescription, OPDPrintPrescription, opdFee });

      setIsSaveBill(CheckUpSaveBill === true || CheckUpSaveBill === "true");
      setIsChecked(CheckUpSavePrescription === true || CheckUpSavePrescription === "true");
      setIsPrintWhileSubmit(OPDPrintPrescription === true || OPDPrintPrescription === "true");

      // Set Bill Amount from OPD Fee
      if (billInput.current && (opdFee || hospitalDefaultBill)) {
        const fee = opdFee || hospitalDefaultBill;
        billInput.current.value = fee;
        if (amountReceivedInput.current) {
          amountReceivedInput.current.value = fee;
          setOutstandingAmount(0); // Assuming full payment by default if auto-filled
        }
      }
    }
  }, [authCtx.hospitalData]);
  // 🟢 Calculate Outstanding Amount (without validation)
  const handleAmountChange = () => {
    const bill = Number(billInput.current?.value || 0);
    const received = Number(amountReceivedInput.current?.value || 0);

    // outstanding can be negative if received > bill (extra paid)
    setOutstandingAmount(bill - received);
  };

  // 🆕 Bill Change Handler → Auto-fill Amount Received also
  const handleBillChange = () => {
    const bill = Number(billInput.current?.value || 0);

    // auto-set received = bill
    amountReceivedInput.current.value = bill;

    setOutstandingAmount(0); // because received = bill initially
  };

  const onGetCheckedDetailsHandler = (checkupData) => {
    setCheckedData(checkupData);
  };

  const onGetMedicineDataHandler = (medicineData) => {
    setPrescrbedMedicines(medicineData);
  };
  // 🆕 Trigger printing after save

  useEffect(() => {

    if (doPrint) {
      printPrescription(checkPatientData, prescrbedMedicines, authCtx.hospitalData, checkedData, new Date().toISOString(), authCtx);
      setDoPrint(false); // reset
    }

  }, [doPrint, checkPatientData, prescrbedMedicines, hospitaData, checkedData]);
  const onSubmitAllPrescriptionData = async () => {
    setIsSubmitting(true); // 🔒 lock button immediately
    if (!billInput.current.value || billInput.current.value.trim() === "") {
      setIsError({ message: t("Bill amount is required"), title: t("Validation Error") });
      billInput.current.focus();
      setIsSubmitting(false);
      return;
    }

    if (!checkedData?.diagnosis || checkedData.diagnosis.trim() === "") {
      setIsError({ message: t("Diagnosis is mandatory"), title: t("Validation Error") });
      setIsSubmitting(false);
      return;
    }

    if (!prescrbedMedicines || prescrbedMedicines.length === 0) {
      setIsError({
        message: t("Prescription not submitted, please add medicines..."),
        title: t("Checkup"),
      });
      setIsSubmitting(false);
      return;
    }

    // attach form values
    checkPatientData.totalAmount = Number(billInput.current.value) || 0;
    checkPatientData.amountReceived = Number(amountReceivedInput.current.value) || 0;
    checkPatientData.outstandingAmount = Number(outstandingAmount) || 0;
    checkPatientData.diagnosis = checkedData ? checkedData.diagnosis : "";
    checkPatientData.complaint = checkedData ? checkedData.complaint : "";
    checkPatientData.advice = checkedData ? checkedData.advice : "";
    checkPatientData.bp1 = checkedData ? checkedData.bps : "";
    checkPatientData.bp2 = checkedData ? checkedData.bpd : "";
    checkPatientData.pulse = checkedData ? checkedData.pulse : "";
    checkPatientData.temperature = checkedData ? checkedData.temp : "";
    checkPatientData.spo2 = checkedData ? checkedData.spo2 : "";
    checkPatientData.weight = checkedData ? checkedData.weight : "";
    
    // Dynamic Speciality Fields
    checkPatientData.lmp = checkedData?.lmp || null;
    checkPatientData.edd = checkedData?.edd || null;
    checkPatientData.length = checkedData?.length || "";
    checkPatientData.hc = checkedData?.hc || "";
    checkPatientData.nextAppointment = checkedData?.nextAppointment || null;
    checkPatientData.notes = checkedData?.notes || "";

    // ✅ Format billingDate in "YYYY-MM-DD HH:mm:ss"
    if (checkedData?.billingDate) {
      const dateObj = new Date(checkedData.billingDate);
      if (!isNaN(dateObj.getTime())) {
        checkPatientData.dateTime = `${dateObj.getFullYear()}-${String(
          dateObj.getMonth() + 1
        ).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")} ${String(
          dateObj.getHours()
        ).padStart(2, "0")}:${String(dateObj.getMinutes()).padStart(2, "0")}:${String(
          dateObj.getSeconds()
        ).padStart(2, "0")}`;
      } else {
        // fallback if invalid date
        const now = new Date();
        checkPatientData.dateTime = `${now.getFullYear()}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(
          now.getHours()
        ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(
          now.getSeconds()
        ).padStart(2, "0")}`;
      }
    } else {
      // fallback if no billingDate present
      const now = new Date();
      checkPatientData.dateTime = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(
        now.getHours()
      ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(
        now.getSeconds()
      ).padStart(2, "0")}`;
    }

    checkPatientData.allergy = checkedData ? checkedData.allergy : "";
    checkPatientData.prescription = prescrbedMedicines;
    checkPatientData.isSavePrescription = isChecked;
    checkPatientData.isSaveBill = isSaveBill;
    const token = getAuthToken();
    const apiChar = getApiChara();
    const fetchApi = `${apiChar}/checkUp/updatePatientPrescription`;

    const fetchWithRetry = async (url, options, retries = 3, backoff = 1000) => {
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Server error: ${response.status}`);
        }
        return response;
      } catch (error) {
        // If it's a network error (not a 500 from server), retry
        if (retries > 0 && !error.message.includes("Server error")) {
          toast.info(t("Network issue detected. Retrying submission... attempt {{count}}", { count: 4 - retries }), { autoClose: 2000 });
          await new Promise(resolve => setTimeout(resolve, backoff));
          return fetchWithRetry(url, options, retries - 1, backoff * 2);
        }
        throw error;
      }
    };

    try {
      const response = await fetchWithRetry(fetchApi, {
        method: "put",
        body: JSON.stringify(checkPatientData),
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
      });

      const result = await response.json();

      // ✅ Update checkPatientData with checkUpId from response for the print
      if (result && result.checkUpId) {
        if (checkPatientData) checkPatientData.checkUpId = result.checkUpId;
      }

      if (isPrintWhileSubmit) {
        // The instruction was to add authCtx.hospitalData to printPrescription calls.
        // The actual printing is handled by the useEffect, which already has it.
        // This commented line is not active.
        // printPrescription(checkPatientData, prescrbedMedicines, hospitaData, checkedData);
        setDoPrint(true);
      }

      setIsSuccess({
        message: t("Patient data submitted successfully."),
        title: t("Checkup Done..."),
      });
    } catch (error) {
      console.error("Fetch operation failed:", error);
      const errorMessage = !navigator.onLine 
        ? t("Network connection failed. Your data is still here, please try again once the internet is restored.")
        : (error.message || t("Something went wrong"));
      
      setIsError({
        message: errorMessage,
        title: !navigator.onLine ? t("Offline") : t("Error..."),
      });
    }
    finally {
      setIsSubmitting(false); // 🔓 unlock after completion (success or fail)
    }

  };

  const handleExport = () => {
    exportPrescriptionPDF({
      patientData: checkPatientData,
      medicines: prescrbedMedicines,
      checkedData: checkedData,
      authCtx: authCtx,
      selectedDateTime: checkedData?.billingDate
    });
  };

  const handleWhatsAppShare = async () => {
    // For WhatsApp, we still need a blob or we need to modify the util to return one.
    // Given the complexity of sharing, let's just use the download for now as it does currently.
    handleExport();

    const patientMobile = checkPatientData?.patientId?.mobile || checkPatientData?.mobile || "";
    const patientName = `${checkPatientData?.patientId?.firstName || ""} ${checkPatientData?.patientId?.lastName || ""}`.trim() || t("Patient");
    const cleanMobile = patientMobile.replace(/\D/g, '');

    let medicineText = "";
    if (prescrbedMedicines && prescrbedMedicines.length > 0) {
      medicineText = `\n\n*${t("Prescribed Medicines:")}*`;
      prescrbedMedicines.forEach((med, index) => {
        const type = med.type ? `${med.type}. ` : "";
        const doses = `(${med.breakFastDose || '-'}/${med.lunchDose || '-'}/${med.dinnerDose || '-'})`;
        medicineText += `\n${index + 1}. ${type}${med.medicineName} - ${med.days} ${t("Days")} ${doses}`;
        if (med.instruction) medicineText += ` [${med.instruction}]`;
      });
    }

    const message = t("Hello {{patientName}}, your prescription from {{hospitalName}} is ready.{{medicineText}}\n\nRegards,\n{{hospitalName}}", {
      patientName: patientName,
      hospitalName: authCtx.hospitalData?.hospitalName || t('the hospital'),
      medicineText: medicineText
    });

    const waUrl = `https://wa.me/91${cleanMobile}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, "_blank");
  };

  if (!checkPatientData) return null;

  const onGetHistoryModelDataonCheckData = (prescription) => {
    if (!prescription) return;
    // Normalize medicine list
    const normalizedMedicines = Array.isArray(prescription)
      ? prescription.map((med) => ({
        id: med?.id,
        medicineName: med?.medicineName || "",
        type: med?.type || "",
        dosage: med?.dosage || "",
        days: med?.days || "",
        breakFastDose: med?.breakFastDose || "",
        lunchDose: med?.lunchDose || "",
        dinnerDose: med?.dinnerDose || "",
        instruction: med?.instruction || "",
      }))
      : [];
    // ➕ Append to existing medicines instead of replacing
    setPrescrbedMedicines((prev) => [...prev, ...normalizedMedicines]);
  };

  return (
    <>
      {success && (
        <Modal
          message={success.message}
          title={success.title}
          OnConfirmBlock={() => setIsSuccess(null)}
          onCloseNavigate={() => navigate("/dashboard/checklist")}
        />
      )}
      {error && (
        <Modal
          message={error.message}
          title={error.title}
          OnConfirmBlock={() => setIsError(null)}
          onCloseNavigate={() => setIsError(null)}
        />
      )}

      {!isOnline && (
        <div className="alert alert-danger text-center mb-2 py-1 mx-3" style={{ borderRadius: '8px', fontWeight: '500' }}>
          <i className="bi bi-wifi-off me-2"></i>
          {t("You are currently offline. Please check your internet connection to submit patient data.")}
        </div>
      )}

      <div className="container-fluid px-3">
        <div className="row">
          <div className="col-md-9 col-12">
            <div className="row boxes">
              <CheckedDetails
                onGetCheckupDetailsHandler={onGetCheckedDetailsHandler}
                onGetMedicineDetailsHandler={onGetMedicineDataHandler}
                prescrbedMedicines={prescrbedMedicines}
              />
              <MedicineDetails
                onGetMedicineDetailsHandler={onGetMedicineDataHandler}
                prescrbedMedicines={prescrbedMedicines}
              />
            </div>

            <div className={classes.actions}>
              <div className={classes.formRow}>
                {/* Save Prescription */}
                <div className={`form-check ${classes.fcPadding}`}>
                  <input
                    type="checkbox"
                    id="saveBillCheck"
                    name="saveBillCheck"
                    checked={isSaveBill}
                    className="m-1"
                    onChange={(e) => {
                      setIsSaveBill(e.target.checked);
                    }}
                  />
                  <label htmlFor="saveBillCheck">{t("Save Bill")}</label>
                </div>
                <div className={`form-check ${classes.fcPadding}`}>
                  <input
                    type="checkbox"
                    id="enableSavePreReason"
                    name="enableSavePreReason"
                    checked={isChecked}
                    className="m-1"
                    onChange={() => setIsChecked(!isChecked)}
                  />
                  <label htmlFor="enableSavePreReason">{t("Save Prescription")}</label>
                </div>

                {/* Bill */}
                <div className={classes.inputGroup}>
                  <label htmlFor="totalAmount" className={`pb-0 pr-2`}>
                    {t("Total Amount:")}
                  </label>
                  <input
                    id="totalAmount"
                    type="number"
                    name="totalAmount"
                    defaultValue={authCtx.hospitalData?.opdFee || authCtx.hospitalDefaultBill || 0}
                    ref={billInput}
                    required
                    step="100"
                    className={classes.formcontrol}
                    onChange={handleBillChange}   // 🆕 updated
                  />
                </div>

                {/* Amount Received */}
                <div className={classes.inputGroup}>
                  <label htmlFor="amountreceived" className={`pb-0 pr-2`}>
                    {t("Amount Received:")}
                  </label>
                  <input
                    id="amountreceived"
                    type="number"
                    name="amountreceived"
                    defaultValue={authCtx.hospitalDefaultBill || 0}
                    ref={amountReceivedInput}
                    required
                    step="50"
                    className={classes.formcontrol}
                    onChange={handleAmountChange}  // 🆕 no validation
                  />
                </div>


                {/* Outstanding */}
                <div className={classes.inputGroup}>
                  <label className={`pb-0 pr-2`}> {t("Outstanding Amount:")}</label>
                  <span>
                    <strong>{outstandingAmount}</strong>
                  </span>
                </div>
              </div>

              {/* 👉 Buttons moved to new row */}
              <div className={classes.buttonRow}>
                {/* Print */}
                <div className={`form-check ${classes.fcPadding}`}>
                  <input
                    type="checkbox"
                    id="printwhilesubmit"
                    name="printwhilesubmit"
                    checked={isPrintWhileSubmit}
                    className="m-1"
                    onChange={() => setIsPrintWhileSubmit(!isPrintWhileSubmit)}
                  />
                  <label htmlFor="printwhilesubmit">{t("Print")}</label>
                </div>

                <button
                  onClick={onSubmitAllPrescriptionData}
                  className={classes.premiumBtn}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FaSave className="me-2" />
                  )}
                  {isSubmitting ? t("Submitting...") : t("Submit")}
                </button>

                <PrescriptionPrint
                  patientData={checkPatientData}
                  medicines={prescrbedMedicines}
                  hospitaData={hospitaData}
                  checkedData={checkedData}
                />

                <button
                  onClick={handleExport}
                  className={`${classes.premiumBtn} ${classes.exportBtn}`}
                  type="button"
                >
                  <FaFileExport className="me-2" /> {t("Export")}
                </button>

                <button
                  onClick={handleWhatsAppShare}
                  className={`${classes.premiumBtn} ${classes.whatsAppBtn}`}
                  type="button"
                  title={t("WhatsApp")}
                >
                  <FaWhatsapp />
                </button>

                <Link className={`${classes.premiumBtn} ${classes.secondaryBtn}`} to="..">
                  <FaArrowLeft className="me-2" /> {t("Back")}
                </Link>
              </div>
            </div>
          </div>

          <div className="col-md-3 col-12">
            <Certificates PatientData={checkPatientData} />
            <PatientHistory PatientData={checkPatientData} onGetHistoryModelData={onGetHistoryModelDataonCheckData} />
          </div>
        </div>
      </div>
      <AIChatBot
        context={`
            Patient: ${checkPatientData?.patientId?.firstName} ${checkPatientData?.patientId?.lastName}
            Age: ${checkPatientData?.patientId?.age}
            Gender: ${checkPatientData?.patientId?.gender}
            Current Vitals: BP(${checkedData?.bps || 'N/A'}/${checkedData?.bpd || 'N/A'}), Pulse(${checkedData?.pulse || 'N/A'}), Temp(${checkedData?.temp || 'N/A'}), SPO2(${checkedData?.spo2 || 'N/A'})
            Current Diagnosis: ${checkedData?.diagnosis || 'Not entered yet'}
            Complaints: ${checkedData?.complaint || 'None'}
            Advice: ${checkedData?.advice || 'None'}
            Medicines Prescribed: ${prescrbedMedicines?.map(m => m.medicineName).join(', ') || 'None'}
        `}
      />
    </>
  );
};

export default CheckUpPatientDetails;
